import { useState } from "react";
import { Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import { IconCheck, IconShoppingBagPlus } from "@tabler/icons-react";
import type { ProductoInventario } from "../types/productoSeleccionado.types";
import { esCantidadValida } from "../utils/productoSeleccionado.utils";
import { CLAVES_COMENTARIO_OBLIGATORIO } from "../constants/productoSeleccionado.constants";

const COLOR_SELECCIONADO = "#e3f2fd";

interface Props<TProducto extends ProductoInventario> {
  producto: TProducto;
  lineas?: number;
  onAgregar: (producto: TProducto, cantidad: number) => void;
  deshabilitado?: boolean;
}

export const FilaProductoCantidad = <TProducto extends ProductoInventario>({
  producto,
  lineas = 0,
  onAgregar,
  deshabilitado = false,
}: Props<TProducto>): JSX.Element => {
  const [texto, setTexto] = useState("");
  const cantidad = Number(texto);
  const valida = texto.trim() !== "" && esCantidadValida(cantidad);
  const agregarDeshabilitado = deshabilitado || !valida;

  const noIncluirProductos = CLAVES_COMENTARIO_OBLIGATORIO.includes(producto.clave!.trim().toUpperCase());

  const agregar = () => {
    if (agregarDeshabilitado) return;
    onAgregar(producto, cantidad);
    setTexto("");
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1.5,
        backgroundColor: lineas > 0 ? COLOR_SELECCIONADO : "transparent",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight="bold" noWrap title={producto.nombre}>
          {producto.nombre}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {producto.clave} {" · "} {producto.unidad_medida}
        </Typography>
        {lineas > 0 && noIncluirProductos && (
          <Chip
            size="small"
            sx={{ ml: 1, background: "#bbfd9d", color: "#384b2f", fontWeight: 500 }}
            label={`${lineas} en la lista`}
          />
        )}
      </Box>

      {lineas > 0 && !noIncluirProductos ? (
        <Chip
          size="small"
          sx={{ ml: 1, background: "#bbfd9d", color: "#384b2f", fontWeight: 500 }}
          icon={<IconCheck size={16} stroke={2} color="#384b2f" />}
          label={`${lineas === 1 && "En la lista"}`}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "end", flexShrink: 0 }}>
          <TextField
            label="Cantidad"
            variant="standard"
            type="number"
            size="small"
            value={texto}
            disabled={deshabilitado}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && agregar()}
            inputProps={{ min: 1, step: 1, "aria-label": `Cantidad de ${producto.nombre}` }}
            sx={{ width: 80 }}
          />
          <IconButton
            disabled={agregarDeshabilitado}
            onClick={agregar}
            aria-label={`Agregar ${producto.nombre}`}
          >
            <IconShoppingBagPlus stroke={2} color={agregarDeshabilitado ? "#a7a7a7" : "#6aa84f"} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};